/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module    public/js/controllers/homepagecontroller.js
 * @class     HomePageController
 * @layer     controller
 * @summary   Builds the landing-page button stack, honouring feature-flags,
 *            email-OAuth status, and admin/manager roles.
 *
 * @description
 * • Waits for Firebase auth.
 * • Checks whether the seller already has a Gmail / Outlook token.
 * • Loads the signed-in user's tenant roles once, to detect admin or manager status.
 * • Generates the visible-button array from a config list, filtering by:
 *     - feature-flags
 *     - minRole ("admin" buttons disappear for normal users)
 *     - email connection (hides Connect-Email when a token exists)
 * • Renders each button – analytics event + target navigation or onClick hook.
 * • Opens the Gmail/Outlook modal chooser when the Connect-Email button fires.
 *
 * Visibility matrix
 * ┌───────────────────────────────┬────────┬────────┬────────┐
 * │ Scenario                      │ Conn?  │ Admin? │ Shown  │
 * ├───────────────────────────────┼────────┼────────┼────────┤
 * │ Standard user, new            │ No     │ No     │ CE ✔  │
 * │ Standard user, connected      │ Yes    │ No     │ –     │
 * │ Admin, new                    │ No     │ Yes    │ CE ✔ │ A ✔ │
 * │ Admin, connected              │ Yes    │ Yes    │ A ✔  │
 * └───────────────────────────────┴────────┴────────┴────────┘
 *
 * @dependencies
 *   • AuthService   (di.auth)       – waitForLogin / currentUserId
 *   • AnalyticsService (di.analytics)
 *   • FeatureFlagService (di.flags)
 *   • FirestoreService (di.fsService)
 *   • roleLoader.js  – loads current-user tenant roles
 *
 * @author    TopSail AI
 * @version   1.1.0
 * @since     2025-05-11
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  doc,
  getDoc,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { docWithTenant, writeWithTenant } from '../utils/firestoreHelpers.js';
import { db } from '/js/core/firebase-service.js';
import { getAppRoles } from '/js/core/roleLoader.js';
import { isGamificationEnabled } from '../admin/tenantSettingsGamification.js';
import { mountHomeGamificationRank } from '../pages/homeGamificationRank.js';
import CallerIdService from '../services/callerIdService.js';

const CRM_OAUTH_PENDING_KEY = 'topsail.crm.oauth.pending';
const SELF_SERVE_SETUP_PATH = '/self-serve/setup.html';
const SELF_SERVE_EDITABLE_STATUSES = new Set(['draft', 'changes_requested']);

/**
 * Decide whether login should resume the tenant's editable guided setup.
 *
 * @param {{ serviceOfferingKey?: string, isAdmin?: boolean, workspace?: Record<string, any>|null }} input - Tenant, role, and onboarding state.
 * @returns {boolean} True when the tenant admin should enter onboarding.
 */
export function shouldOpenSelfServeOnboarding({
  serviceOfferingKey = '',
  isAdmin = false,
  workspace = null,
} = {}) {
  const offering = String(serviceOfferingKey).trim().toLowerCase();
  const status = String(workspace?.setup?.status || '')
    .trim()
    .toLowerCase();
  return (
    (offering === 'self_serve' ||
      (offering === 'leads_only' && isAdmin === true)) &&
    SELF_SERVE_EDITABLE_STATUSES.has(status)
  );
}

export default class HomePageController {
  #auth;
  #analytics;
  #apiClient;
  #flags;
  #crmConnection;
  #callerId;
  #callables;
  #navigateTo;
  #container;
  #tenantId;
  #userId;
  #emailOptOut = false;

  /**
   * Create the home-page controller and its connection-flow dependencies.
   *
   * @param {object} dependencies - Home-page services.
   * @param {object} dependencies.authService - Signed-in user service.
   * @param {object|null} dependencies.analyticsService - Optional analytics service.
   * @param {object|null} dependencies.apiClient - Authenticated HTTP client.
   * @param {object|null} dependencies.featureFlagService - Feature flag service.
   * @param {object|null} dependencies.crmConnectionService - CRM connection service.
   * @param {object|null} dependencies.callerIdService - Optional injected caller-ID service.
   * @param {object|null} dependencies.callables - Optional Firebase callable registry.
   * @param {(path: string) => void} dependencies.navigateTo - Optional navigation adapter.
   */
  constructor({
    authService,
    analyticsService,
    apiClient,
    featureFlagService,
    crmConnectionService,
    callerIdService,
    callables,
    navigateTo,
  }) {
    this.#auth = authService;
    this.#analytics = analyticsService;
    this.#apiClient = apiClient;
    this.#flags = featureFlagService;
    this.#crmConnection = crmConnectionService;
    this.#callerId =
      callerIdService ||
      (apiClient?.fetchJson ? new CallerIdService(apiClient) : null);
    this.#callables = callables || {};
    this.#navigateTo =
      navigateTo || ((path) => window.location.assign(String(path)));

    this.#container = document.getElementById('landingButtons');
    if (!this.#container)
      throw new Error('[HomePageController] missing #landingButtons');
  }

  /* ───────── public ───────── */
  async init() {
    /* 1️⃣ auth + context */
    const user = await this.#auth.requireAuth();
    const tenantId = user.tenantId;
    this.#userId = user.uid;
    this.#tenantId = tenantId;
    if (!tenantId) {
      console.error('Missing tenant ID');
      window.location.href = '/index.html';
      return;
    }

    let generalData = {};
    let serviceOfferingKey = 'full_service';
    const [generalResult, tenantResult] = await Promise.allSettled([
      getDoc(docWithTenant(tenantId, 'AppConfig/general')),
      getDoc(doc(db, 'tenants', tenantId)),
    ]);
    if (generalResult.status === 'fulfilled') {
      const generalSnap = generalResult.value;
      generalData = generalSnap?.exists?.() ? generalSnap.data() || {} : {};
    } else {
      console.warn(
        '[HomePageController] failed to read /AppConfig/general',
        generalResult.reason
      );
    }
    if (tenantResult.status === 'fulfilled') {
      const tenantSnap = tenantResult.value;
      serviceOfferingKey = String(
        tenantSnap?.exists?.()
          ? tenantSnap.data()?.serviceOffering?.key || 'full_service'
          : 'full_service'
      )
        .trim()
        .toLowerCase();
    } else {
      console.warn(
        '[HomePageController] failed to read tenant service offering',
        tenantResult.reason
      );
    }

    /* roles + guided-offering login routing */
    const rolesDoc = await getAppRoles().catch(() => null);
    const isAdmin = rolesDoc && (rolesDoc.admins ?? []).includes(user.uid);
    const isManager = rolesDoc && (rolesDoc.managers ?? []).includes(user.uid);

    if (
      (serviceOfferingKey === 'self_serve' ||
        (serviceOfferingKey === 'leads_only' && isAdmin)) &&
      typeof this.#callables.selfServeGetOnboarding === 'function'
    ) {
      try {
        const response = await this.#callables.selfServeGetOnboarding({
          tenantId,
        });
        const workspace = response?.data ?? response;
        if (
          shouldOpenSelfServeOnboarding({
            serviceOfferingKey,
            isAdmin,
            workspace,
          })
        ) {
          this.#navigateTo(SELF_SERVE_SETUP_PATH);
          return;
        }
      } catch (err) {
        console.warn(
          '[HomePageController] guided onboarding status unavailable',
          err
        );
      }
    }

    /* email connection */
    let emailConnectionNeedsReconsent = false;
    const hasEmailConn =
      serviceOfferingKey === 'leads_only'
        ? true
        : await getDoc(docWithTenant(tenantId, `EmailOAuth/${user.uid}`), {
            source: 'server',
          })
            .then((snap) => {
              if (!snap.exists()) return false;
              const oauthData = snap.data() || {};
              emailConnectionNeedsReconsent =
                oauthData.revoked === true || oauthData.needsReconsent === true;
              return !emailConnectionNeedsReconsent;
            })
            .catch(() => false);

    let callerIdState = null;
    try {
      callerIdState =
        serviceOfferingKey === 'leads_only'
          ? null
          : await this.#callerId?.getStatus?.({ tenantId });
    } catch (err) {
      console.warn('[HomePageController] caller ID status unavailable', err);
    }

    /* opt out preference */
    try {
      const prefSnap = await getDoc(
        docWithTenant(tenantId, `UserPreferences/${user.uid}`),
        { source: 'server' }
      );
      this.#emailOptOut =
        prefSnap?.exists?.() && prefSnap.data()?.emailOptOut === true;
    } catch (err) {
      console.warn('[HomePageController] failed to read email opt-out', err);
    }

    /* opt out preference */
    try {
      const prefSnap = await getDoc(
        docWithTenant(tenantId, `UserPreferences/${user.uid}`),
        { source: 'server' }
      );
      this.#emailOptOut =
        prefSnap?.exists?.() && prefSnap.data()?.emailOptOut === true;
    } catch (err) {
      console.warn('[HomePageController] failed to read email opt-out', err);
    }

    /* CRM provider */
    const pendingCrmOAuthProvider = this.#readCrmOAuthPendingProvider();
    const crmLessForced =
      generalData?.skipCrmCreates === true &&
      generalData?.crmLessImport === true;
    let crmProvider = crmLessForced
      ? 'local'
      : await this.#getCrmProvider(user.uid);
    if (crmProvider === 'local' && pendingCrmOAuthProvider && !crmLessForced) {
      crmProvider = await this.#getCrmProvider(user.uid);
    }
    if (pendingCrmOAuthProvider) {
      this.#clearCrmOAuthPendingProvider();
    }

    const hasCrmConn =
      crmProvider === 'hubspot' || crmProvider === 'salesforce';
    console.log(
      '[HomePageController] crmProvider ➜',
      crmProvider,
      'hasCrmConn ➜',
      hasCrmConn
    );

    const shouldPromptEmail =
      !hasEmailConn && (!this.#emailOptOut || emailConnectionNeedsReconsent);
    const shouldPromptCallerId = callerIdState?.callerIdSetupRequired === true;
    const openCallerIdSetup = () => {
      if (shouldPromptCallerId) {
        this.#openCallerIdModal(callerIdState);
      }
    };
    let shouldOpenEmail = shouldPromptEmail && hasCrmConn;

    if (crmProvider === 'local') {
      if (!crmLessForced) {
        const onCrmModalClose = () => {
          if (shouldPromptEmail) {
            this.#openEmailModal(openCallerIdSetup);
          } else {
            openCallerIdSetup();
          }
        };
        const crmModalOpened = this.#openCrmModal(onCrmModalClose) === true;

        if (!crmModalOpened && shouldPromptEmail) {
          shouldOpenEmail = true;
        }
      } else if (shouldPromptEmail) {
        shouldOpenEmail = true;
      }
    } else if (shouldPromptEmail) {
      shouldOpenEmail = true;
    }

    /* 2️⃣ build visible buttons */
    const buttons = this.#buttonConfig().filter((b) => {
      if (b.minRole === 'admin' && !isAdmin) return false;
      if (b.minRole === 'manager' && !(isAdmin || isManager)) return false;
      if (b.flag && !this.#flags?.isEnabled(b.flag)) return false;
      if (
        b.requiresServiceOffering &&
        b.requiresServiceOffering !== serviceOfferingKey
      ) {
        return false;
      }
      if (b.excludesServiceOffering === serviceOfferingKey) return false;
      if (
        b.requiresAccountBasedSelling &&
        generalData.accountBasedSellingEnabled !== true
      ) {
        return false;
      }
      return true;
    });

    /* 3️⃣ render */
    buttons.forEach((b) => this.#render(b));
    if (isGamificationEnabled(generalData)) {
      mountHomeGamificationRank({
        container: this.#container,
        apiClient: this.#apiClient,
        analytics: this.#analytics,
      }).catch((err) => {
        console.warn('[HomePageController] gamification rank unavailable', err);
      });
    }

    /* 4️⃣ prompt for email connection if missing */
    if (shouldOpenEmail) {
      this.#openEmailModal(openCallerIdSetup);
    } else if (hasCrmConn || generalData?.skipCrmCreates === true) {
      openCallerIdSetup();
    }
  }

  /**
   * Determine the user's connected CRM provider using the dedicated
   * sanitized server endpoint without reading provider credentials.
   *
   * @param {string} userId
   * @returns {Promise<'hubspot' | 'salesforce' | 'local' | null>} Null means unavailable.
   */
  async #getCrmProvider(userId) {
    try {
      const status = await this.#apiClient.fetchJson(
        `/crmConnectionStatus?tenantId=${encodeURIComponent(this.#tenantId)}&userId=${encodeURIComponent(userId)}`,
        { auth: true }
      );
      if (
        typeof status?.hubspot?.connected !== 'boolean' ||
        typeof status?.salesforce?.connected !== 'boolean'
      ) {
        throw new Error('CRM status unavailable');
      }
      if (status.salesforce.connected) return 'salesforce';
      if (status.hubspot.connected) return 'hubspot';
      return 'local';
    } catch (err) {
      console.warn('[HomePageController] CRM status unavailable', err);
      return null;
    }
  }

  /**
   * Read an OAuth-in-progress marker set before redirecting to CRM consent.
   *
   * @returns {string|null}
   */
  #readCrmOAuthPendingProvider() {
    try {
      return sessionStorage.getItem(CRM_OAUTH_PENDING_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Clear the OAuth-in-progress marker after home-page bootstrap.
   *
   * @returns {void}
   */
  #clearCrmOAuthPendingProvider() {
    try {
      sessionStorage.removeItem(CRM_OAUTH_PENDING_KEY);
    } catch {
      // best-effort only
    }
  }

  /* ───────── modal helpers ───────── */
  /**
   * Persist a user-level preference to skip future email connection prompts.
   *
   * @returns {Promise<void>}
   */
  async #persistEmailOptOut() {
    if (!this.#tenantId || !this.#userId) {
      console.warn(
        '[HomePageController] missing tenant or user id for opt-out write'
      );
      return;
    }

    const prefsRef = docWithTenant(
      this.#tenantId,
      `UserPreferences/${this.#userId}`
    );
    await writeWithTenant(
      this.#tenantId,
      prefsRef,
      { emailOptOut: true },
      { merge: true }
    );
  }

  /**
   * Remove a previously stored email opt-out so future disconnects will
   * re-enable connection prompts.
   *
   * @returns {Promise<void>}
   */
  async #clearEmailOptOut() {
    if (!this.#tenantId || !this.#userId) return;

    const prefsRef = docWithTenant(
      this.#tenantId,
      `UserPreferences/${this.#userId}`
    );
    await writeWithTenant(
      this.#tenantId,
      prefsRef,
      { emailOptOut: false },
      { merge: true }
    );
    this.#emailOptOut = false;
  }

  #openEmailModal(onClose) {
    const m = document.getElementById('emailProviderModal');
    if (!m) return;
    m.classList.add('modal--open');
    m.style.display = 'grid';
    m.setAttribute('aria-hidden', 'false');

    const emailSvc = window.di?.emailConnectionService;
    const loading = window.di?.loading;
    const errorUI = window.di?.errorUI;

    const gmailBtn = m.querySelector('#btnGmail');
    if (gmailBtn) {
      gmailBtn.onclick = async () => {
        if (gmailBtn.disabled) return;
        gmailBtn.disabled = true;
        this.#analytics?.track?.('OAuth - Gmail start');
        m.classList.remove('modal--open');
        m.style.display = '';
        m.setAttribute('aria-hidden', 'true');
        loading?.show?.('tasks', 'Connecting to Gmail…');

        try {
          await emailSvc?.connectGmail?.();
        } catch (err) {
          console.error('[HomePageController] Gmail OAuth failed', err);
          loading?.hide?.('tasks');
          errorUI?.global?.({
            text: "We couldn't connect to Gmail. Please try again.",
            type: 'error',
          });
          gmailBtn.disabled = false;
          this.#openEmailModal(onClose);
          return;
        }
        await this.#clearEmailOptOut();
        gmailBtn.disabled = false;
      };
    }

    const outlookBtn = m.querySelector('#btnOutlook');
    if (outlookBtn) {
      outlookBtn.onclick = async () => {
        if (outlookBtn.disabled) return;
        outlookBtn.disabled = true;
        this.#analytics?.track?.('OAuth - Outlook start');
        m.classList.remove('modal--open');
        m.style.display = '';
        m.setAttribute('aria-hidden', 'true');
        loading?.show?.('tasks', 'Connecting to Outlook…');

        try {
          await emailSvc?.connectOutlook?.();
        } catch (err) {
          console.error('[HomePageController] Outlook OAuth failed', err);
          loading?.hide?.('tasks');
          errorUI?.global?.({
            text: "We couldn't connect to Outlook. Please try again.",
            type: 'error',
          });
          outlookBtn.disabled = false;
          this.#openEmailModal(onClose);
          return;
        }
        await this.#clearEmailOptOut();
        outlookBtn.disabled = false;
      };
    }

    const closeBtn = m.querySelector('#btnEmailModalClose');
    if (closeBtn) {
      closeBtn.onclick = () => {
        m.classList.remove('modal--open');
        m.style.display = '';
        m.setAttribute('aria-hidden', 'true');
        if (typeof onClose === 'function') onClose();
      };
    }

    const optOutBtn = m.querySelector('#btnEmailOptOut');
    if (optOutBtn) {
      optOutBtn.onclick = async () => {
        if (optOutBtn.disabled) return;
        optOutBtn.disabled = true;
        this.#analytics?.track?.('Email - Opt out');

        try {
          await this.#persistEmailOptOut();
          this.#emailOptOut = true;
          m.classList.remove('modal--open');
          m.style.display = '';
          m.setAttribute('aria-hidden', 'true');
          if (typeof onClose === 'function') onClose();
        } catch (err) {
          console.error('[HomePageController] Opt-out save failed', err);
          errorUI?.global?.({
            text: "We couldn't save your preference. Please try again.",
            type: 'error',
          });
          optOutBtn.disabled = false;
        }
      };
    }
  }

  /**
   * Open the guided Topsail-versus-company-phone caller-ID setup.
   *
   * @param {Record<string, any>} initialState - Current backend connection state.
   * @returns {void}
   */
  #openCallerIdModal(initialState = {}) {
    const modal = document.getElementById('callerIdModal');
    if (!modal) return;
    const choices = modal.querySelector('#callerIdChoices');
    const phonePanel = modal.querySelector('#callerIdPhonePanel');
    const phoneInput = modal.querySelector('#callerIdPhoneInput');
    const instructions = modal.querySelector('#callerIdInstructions');
    const code = modal.querySelector('#callerIdValidationCode');
    const feedback = modal.querySelector('#callerIdFeedback');
    const working = modal.querySelector('#callerIdWorking');
    const workingText = modal.querySelector('#callerIdWorkingText');
    const cancelBtn = modal.querySelector('#btnCallerIdCancel');
    const alternateBtn = modal.querySelector('#btnCallerIdAlternate');
    const primaryBtn = modal.querySelector('#btnCallerIdPrimary');
    const choiceInputs = Array.from(
      modal.querySelectorAll('input[name="callerIdPreference"]')
    );
    let state = initialState;
    let polling = false;

    const setOpen = (open) => {
      modal.classList.toggle('modal--open', open);
      modal.style.display = open ? 'grid' : '';
      modal.setAttribute('aria-hidden', open ? 'false' : 'true');
    };
    const setBusy = (busy) => {
      choiceInputs.forEach((input) => {
        input.disabled = busy;
      });
      if (phoneInput) phoneInput.disabled = busy;
      if (cancelBtn) cancelBtn.disabled = busy;
      if (alternateBtn) alternateBtn.disabled = busy;
      if (primaryBtn) primaryBtn.disabled = busy;
    };
    /**
     * Show or clear the caller-ID operation indicator.
     *
     * @param {string} message - Visible progress message, or empty to stop.
     * @returns {void}
     */
    const setWorking = (message) => {
      const active = Boolean(message);
      modal.setAttribute('aria-busy', active ? 'true' : 'false');
      if (working) working.hidden = !active;
      if (workingText) workingText.textContent = message || '';
    };
    const selectedPreference = () =>
      choiceInputs.find((input) => input.checked)?.value || '';
    const renderChoice = () => {
      const preference = selectedPreference();
      if (phonePanel) phonePanel.hidden = preference !== 'own';
      if (primaryBtn) {
        primaryBtn.disabled = !preference;
        primaryBtn.textContent =
          preference === 'own' ? 'Verify My Number' : 'Use Topsail Number';
      }
      if (alternateBtn) alternateBtn.hidden = true;
      if (instructions) instructions.hidden = true;
      if (feedback) feedback.textContent = '';
      setWorking('');
    };
    const pollForVerification = async () => {
      if (polling) return;
      polling = true;
      const deadline = Date.now() + 2 * 60 * 1000;
      while (polling && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (!polling) return;
        try {
          state = await this.#callerId.getStatus({ tenantId: this.#tenantId });
        } catch {
          continue;
        }
        const verificationStatus = state?.externalCallerId?.status;
        if (verificationStatus === 'verified') {
          polling = false;
          setWorking('');
          if (feedback) {
            feedback.textContent = `Number connected •••• ${state.externalCallerId.lastFour || ''}`;
            feedback.className =
              'caller-id-feedback caller-id-feedback--success';
          }
          if (primaryBtn) {
            primaryBtn.textContent = 'Done';
            primaryBtn.disabled = false;
          }
          if (cancelBtn) cancelBtn.disabled = false;
          if (alternateBtn) alternateBtn.hidden = true;
          return;
        }
        if (verificationStatus === 'failed') {
          polling = false;
          setWorking('');
          if (feedback) {
            feedback.textContent =
              'The verification was not completed. Try the call again or use a Topsail number.';
            feedback.className = 'caller-id-feedback caller-id-feedback--error';
          }
          if (primaryBtn) {
            primaryBtn.textContent = 'Try Again';
            primaryBtn.disabled = false;
          }
          if (alternateBtn) {
            alternateBtn.hidden = false;
            alternateBtn.disabled = false;
          }
          if (cancelBtn) cancelBtn.disabled = false;
          return;
        }
      }
      polling = false;
      setWorking('');
      if (feedback) {
        feedback.textContent =
          'We are still waiting for verification. You can try again if the call did not arrive.';
        feedback.className = 'caller-id-feedback caller-id-feedback--error';
      }
      if (primaryBtn) {
        primaryBtn.textContent = 'Try Again';
        primaryBtn.disabled = false;
      }
      if (alternateBtn) {
        alternateBtn.hidden = false;
        alternateBtn.disabled = false;
      }
      if (cancelBtn) cancelBtn.disabled = false;
    };

    choiceInputs.forEach((input) => {
      input.checked = false;
      input.onchange = renderChoice;
    });
    if (phonePanel) phonePanel.hidden = true;
    if (instructions) instructions.hidden = true;
    if (alternateBtn) alternateBtn.hidden = true;
    if (code) code.textContent = '';
    if (feedback) {
      feedback.textContent = '';
      feedback.className = 'caller-id-feedback';
    }
    setWorking('');
    if (primaryBtn) {
      primaryBtn.textContent = 'Continue';
      primaryBtn.disabled = true;
      primaryBtn.onclick = async () => {
        if (state?.externalCallerId?.status === 'verified') {
          polling = false;
          setOpen(false);
          return;
        }
        const preference = selectedPreference();
        if (!preference) return;
        setBusy(true);
        setWorking('Saving your caller ID choice…');
        try {
          state = await this.#callerId.setPreference({
            tenantId: this.#tenantId,
            preference,
          });
          if (preference === 'topsail') {
            this.#analytics?.track?.('Caller ID - Topsail selected');
            polling = false;
            setWorking('');
            setOpen(false);
            return;
          }
          setWorking('Starting your verification call…');
          const verification = await this.#callerId.startVerification({
            tenantId: this.#tenantId,
            phoneNumber: phoneInput?.value || '',
          });
          this.#analytics?.track?.('Caller ID - Own number verification start');
          if (verification.status === 'verified') {
            state = {
              ...state,
              callerIdSetupRequired: false,
              externalCallerId: {
                ...(state?.externalCallerId || {}),
                status: 'verified',
                lastFour: verification.lastFour || '',
              },
            };
            polling = false;
            setWorking('');
            if (choices) choices.hidden = true;
            if (phonePanel) phonePanel.hidden = true;
            if (instructions) instructions.hidden = true;
            if (code) code.textContent = '';
            if (feedback) {
              feedback.textContent = `Number connected •••• ${verification.lastFour || ''}`;
              feedback.className =
                'caller-id-feedback caller-id-feedback--success';
            }
            if (primaryBtn) {
              primaryBtn.textContent = 'Done';
              primaryBtn.disabled = false;
            }
            if (cancelBtn) cancelBtn.disabled = false;
            if (alternateBtn) alternateBtn.hidden = true;
            return;
          }
          if (choices) choices.hidden = true;
          if (phonePanel) phonePanel.hidden = true;
          if (instructions) instructions.hidden = false;
          if (code) code.textContent = verification.validationCode || '';
          if (feedback) {
            feedback.textContent = `Twilio is calling ${verification.maskedNumber}. Answer and enter this code on the keypad.`;
            feedback.className = 'caller-id-feedback';
          }
          if (primaryBtn) primaryBtn.textContent = 'Waiting for verification…';
          setWorking('Waiting for Twilio to confirm your number…');
          void pollForVerification();
        } catch (err) {
          console.error('[HomePageController] caller ID setup failed', err);
          setWorking('');
          if (feedback) {
            feedback.textContent =
              "We couldn't start the verification call. Check the number and try again.";
            feedback.className = 'caller-id-feedback caller-id-feedback--error';
          }
          setBusy(false);
        }
      };
    }
    if (alternateBtn) {
      alternateBtn.onclick = async () => {
        setBusy(true);
        setWorking('Switching to a Topsail number…');
        try {
          await this.#callerId.setPreference({
            tenantId: this.#tenantId,
            preference: 'topsail',
          });
          polling = false;
          setWorking('');
          setOpen(false);
        } catch (err) {
          setWorking('');
          setBusy(false);
          if (feedback) feedback.textContent = err?.message || 'Try again.';
        }
      };
    }
    if (cancelBtn) {
      cancelBtn.onclick = () => {
        polling = false;
        setWorking('');
        setOpen(false);
      };
    }
    setOpen(true);
  }

  #openCrmModal(onClose) {
    const m = document.getElementById('crmProviderModal');
    if (!m) return false;
    m.classList.add('modal--open');
    m.style.display = 'grid';
    m.setAttribute('aria-hidden', 'false');

    const loading = window.di?.loading;
    const errorUI = window.di?.errorUI;
    const crmSvc = this.#crmConnection || window.di?.crmConnectionService;

    const hubBtn = m.querySelector('#btnHubSpot');
    if (hubBtn) {
      hubBtn.onclick = async () => {
        if (hubBtn.disabled) return;
        hubBtn.disabled = true;
        this.#analytics?.track?.('CRM - HubSpot start');
        m.classList.remove('modal--open');
        m.style.display = '';
        m.setAttribute('aria-hidden', 'true');
        loading?.show?.('tasks', 'Connecting to HubSpot…');

        try {
          await crmSvc?.connectHubSpot?.();
          if (typeof onClose === 'function') onClose();
        } catch (err) {
          console.error('[HomePageController] HubSpot connect failed', err);
          loading?.hide?.('tasks');
          errorUI?.global?.({
            text: "We couldn't connect to HubSpot. Please try again.",
            type: 'error',
          });
          hubBtn.disabled = false;
          this.#openCrmModal(onClose);
          return;
        }
        hubBtn.disabled = false;
      };
    }

    const sfBtn = m.querySelector('#btnSalesforce');
    if (sfBtn) {
      sfBtn.onclick = async () => {
        if (sfBtn.disabled) return;
        sfBtn.disabled = true;
        this.#analytics?.track?.('CRM - Salesforce start');
        m.classList.remove('modal--open');
        m.style.display = '';
        m.setAttribute('aria-hidden', 'true');
        loading?.show?.('tasks', 'Connecting to Salesforce…');

        try {
          await crmSvc?.connectSalesforce?.();
          if (typeof onClose === 'function') onClose();
        } catch (err) {
          console.error('[HomePageController] Salesforce connect failed', err);
          loading?.hide?.('tasks');
          errorUI?.global?.({
            text: "We couldn't connect to Salesforce. Please try again.",
            type: 'error',
          });
          sfBtn.disabled = false;
          this.#openCrmModal(onClose);
          return;
        }
        sfBtn.disabled = false;
      };
    }

    const closeBtn = m.querySelector('#btnCrmModalClose');
    if (closeBtn) {
      closeBtn.onclick = () => {
        m.classList.remove('modal--open');
        m.style.display = '';
        m.setAttribute('aria-hidden', 'true');
        if (typeof onClose === 'function') onClose();
      };
    }
    return true;
  }

  /* ───────── button list ───────── */
  #buttonConfig() {
    const buttons = [
      {
        id: 'queue',
        text: 'Start',
        target: 'focusmode.html',
        className: 'btn btn-pill btn--orange',
        minRole: 'user',
        event: 'Landing - Queue',
        excludesServiceOffering: 'leads_only',
      },

      // TODO: replace with real navigation once meetings.html is live (ticket #123)
      //{ id: "meetings", text: "Mtgs", target: null,
      //  className: "btn btn-pill btn--navy",  minRole: "user",
      //  event: "Landing - Meetings",
      //  onClick: (btn) => this.#notifyUpcoming("Meetings", btn) },

      {
        id: 'leads',
        text: 'Contacts',
        target: 'leads.html',
        className: 'btn btn-pill btn--teal',
        minRole: 'user',
        event: 'Landing - Leads',
      },

      {
        id: 'strategicAccounts',
        text: 'Accounts',
        target: 'strategic-accounts.html',
        className: 'btn btn-pill btn--teal',
        minRole: 'user',
        requiresAccountBasedSelling: true,
        event: 'Landing - Strategic Accounts',
      },

      {
        id: 'reporting',
        text: 'Reports',
        target: 'reports.html',
        className: 'btn btn-pill btn--navy',
        minRole: 'seller',
        event: 'Landing - Reports',
      },

      {
        id: 'admin',
        text: 'Admin',
        target: 'admin/dashboard.html',
        className: 'btn btn-pill btn--purple',
        minRole: 'admin',
        event: 'Landing - Admin',
      },
      // email connection handled by onboarding flow – modal opens automatically
    ];

    return buttons;
  }

  /* ───────── dom renderer ───────── */
  #render({ id, text, target, event, onClick, className }) {
    const btn = document.createElement('button');
    btn.id = `landingBtn-${id}`;
    btn.className = className || 'btn btn-pill btn--navy';
    btn.textContent = text;

    btn.addEventListener('click', () => {
      this.#analytics?.track?.(event);
      onClick ? onClick(btn) : (window.location.href = target);
    });

    this.#container.appendChild(btn);
  }
}

/* ───────────────────────── On-boarding guard (optional global helper) ───────────────────────── */
export async function runOnboardingGuard(di) {
  const [hasEmail, crmProvider] = await Promise.all([
    di.emailConnectionService?.getConnectedProvider?.(),
    di.crmConnectionService?.getConnectedProvider?.(),
  ]);

  // Step 1 → ensure CRM connection
  if (!crmProvider) return di.ui.showCrmConnectModal();

  // Step 2 → prompt for email once CRM is linked
  if (!hasEmail) return di.ui.showEmailConnectModal();
}
